'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface Article {
  id: string;
  title: string;
  content: string;
  category: string;
  tags: string[];
  lastUpdated: string;
  createdAt: string;
  viewCount: number;
  author: string;
  published: boolean;
}

export interface Category {
  id: string;
  name: string;
  icon: string;
  description: string;
}

interface ArticlesContextType {
  articles: Article[];
  categories: Category[];
  loading: boolean;
  selectedArticleId: string | null;
  setSelectedArticleId: (id: string | null) => void;
  createArticle: (data: Omit<Article, 'id' | 'createdAt' | 'lastUpdated' | 'viewCount'>) => Promise<Article>;
  updateArticle: (id: string, data: Partial<Article>) => Promise<boolean>;
  deleteArticle: (id: string) => Promise<boolean>;
  incrementView: (id: string) => void;
  createCategory: (data: Omit<Category, 'id'>) => Promise<Category>;
  updateCategory: (id: string, data: Partial<Category>) => Promise<boolean>;
  deleteCategory: (id: string) => Promise<boolean>;
  getArticleById: (id: string) => Article | undefined;
  getMostViewed: (limit?: number) => Article[];
  getRecentlyUpdated: (limit?: number) => Article[];
  searchArticles: (query: string) => Article[];
  refreshArticles: () => Promise<void>;
}

const ArticlesContext = createContext<ArticlesContextType | null>(null);

function mapRow(row: Record<string, unknown>): Article {
  return {
    id: row.id as string,
    title: row.title as string,
    content: row.content as string,
    category: row.category as string,
    tags: (row.tags as string[]) || [],
    lastUpdated: row.last_updated as string,
    createdAt: row.created_at as string,
    viewCount: (row.view_count as number) || 0,
    author: (row.author as string) || 'Admin',
    published: (row.published as boolean) || false,
  };
}

function mapCategoryRow(row: Record<string, unknown>): Category {
  return {
    id: row.id as string,
    name: row.name as string,
    icon: (row.icon as string) || '📄',
    description: (row.description as string) || '',
  };
}

const DEFAULT_CATEGORIES: Category[] = [
  { id: 'cat-order-processing', name: 'Order Processing', icon: '📦', description: 'Order workflows and SOPs' },
  { id: 'cat-email-chat', name: 'Email & Chat Workflow', icon: '💬', description: 'Communication templates' },
  { id: 'cat-cancellation', name: 'Cancellation', icon: '📋', description: 'Cancellation procedures' },
  { id: 'cat-complaints', name: 'Complaints', icon: '⚠️', description: 'Escalation and refund procedures' },
  { id: 'cat-general', name: 'General Knowledge', icon: '📚', description: 'Company overview and FAQs' },
  { id: 'cat-materials', name: 'Materials & Finishes', icon: '✨', description: 'Product knowledge hub' },
];

export function ArticlesProvider({ children }: { children: ReactNode }) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);

  const supabase = createClient();

  const refreshArticles = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching articles:', error.message);
        return;
      }
      setArticles((data || []).map(mapRow));
    } catch (err) {
      console.error('Failed to fetch articles:', err);
    }
  }, [supabase]);

  const refreshCategories = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching categories:', error.message);
        return;
      }
      if (data && data.length > 0) {
        setCategories(data.map(mapCategoryRow));
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [supabase]);

  useEffect(() => {
    async function init() {
      setLoading(true);
      await Promise.all([refreshArticles(), refreshCategories()]);
      setLoading(false);
    }
    init();
  }, [refreshArticles, refreshCategories]);

  async function createArticle(data: Omit<Article, 'id' | 'createdAt' | 'lastUpdated' | 'viewCount'>): Promise<Article> {
    const now = new Date().toISOString();
    const id = `art-${Date.now()}`;
    const row = {
      id,
      title: data.title,
      content: data.content,
      category: data.category,
      tags: data.tags,
      published: data.published,
      author: data.author,
      view_count: 0,
      last_updated: now,
      created_at: now,
    };

    const { data: inserted, error } = await supabase
      .from('articles')
      .insert(row)
      .select()
      .single();

    if (error) {
      console.error('Error creating article:', error.message);
      throw new Error(error.message);
    }

    const article = mapRow(inserted);
    setArticles(prev => [article, ...prev]);
    return article;
  }

  async function updateArticle(id: string, data: Partial<Article>): Promise<boolean> {
    const patch: Record<string, unknown> = {};
    if (data.title !== undefined) patch.title = data.title;
    if (data.content !== undefined) patch.content = data.content;
    if (data.category !== undefined) patch.category = data.category;
    if (data.tags !== undefined) patch.tags = data.tags;
    if (data.published !== undefined) patch.published = data.published;
    if (data.author !== undefined) patch.author = data.author;
    if (data.viewCount !== undefined) patch.view_count = data.viewCount;
    patch.last_updated = new Date().toISOString();

    const { error } = await supabase
      .from('articles')
      .update(patch)
      .eq('id', id);

    if (error) {
      console.error('Error updating article:', error.message);
      return false;
    }

    setArticles(prev =>
      prev.map(a =>
        a.id === id
          ? { ...a, ...data, lastUpdated: patch.last_updated as string }
          : a
      )
    );
    return true;
  }

  async function deleteArticle(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('articles')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting article:', error.message);
      return false;
    }

    setArticles(prev => prev.filter(a => a.id !== id));
    return true;
  }

  function incrementView(id: string) {
    const article = articles.find(a => a.id === id);
    if (!article) return;
    const newCount = article.viewCount + 1;
    supabase
      .from('articles')
      .update({ view_count: newCount })
      .eq('id', id)
      .then(({ error }) => {
        if (!error) {
          setArticles(prev =>
            prev.map(a => a.id === id ? { ...a, viewCount: newCount } : a)
          );
        }
      });
  }

  async function createCategory(data: Omit<Category, 'id'>): Promise<Category> {
    const id = `cat-${Date.now()}`;
    const { data: inserted, error } = await supabase
      .from('categories')
      .insert({ id, ...data })
      .select()
      .single();

    if (error) {
      console.error('Error creating category:', error.message);
      throw new Error(error.message);
    }

    const cat = mapCategoryRow(inserted);
    setCategories(prev => [...prev, cat]);
    return cat;
  }

  async function updateCategory(id: string, data: Partial<Category>): Promise<boolean> {
    const { error } = await supabase
      .from('categories')
      .update(data)
      .eq('id', id);

    if (error) {
      console.error('Error updating category:', error.message);
      return false;
    }

    setCategories(prev => prev.map(c => c.id === id ? { ...c, ...data } : c));
    return true;
  }

  async function deleteCategory(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('categories')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting category:', error.message);
      return false;
    }

    setCategories(prev => prev.filter(c => c.id !== id));
    return true;
  }

  function getArticleById(id: string) {
    return articles.find(a => a.id === id);
  }

  function getMostViewed(limit = 5) {
    return [...articles]
      .filter(a => a.published)
      .sort((a, b) => b.viewCount - a.viewCount)
      .slice(0, limit);
  }

  function getRecentlyUpdated(limit = 5) {
    return [...articles]
      .filter(a => a.published)
      .sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
      .slice(0, limit);
  }

  function searchArticles(query: string): Article[] {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return articles.filter(
      a =>
        a.published &&
        (a.title.toLowerCase().includes(q) ||
          a.content.toLowerCase().includes(q) ||
          a.tags.some(t => t.toLowerCase().includes(q)))
    );
  }

  return (
    <ArticlesContext.Provider
      value={{
        articles,
        categories,
        loading,
        selectedArticleId,
        setSelectedArticleId,
        createArticle,
        updateArticle,
        deleteArticle,
        incrementView,
        createCategory,
        updateCategory,
        deleteCategory,
        getArticleById,
        getMostViewed,
        getRecentlyUpdated,
        searchArticles,
        refreshArticles,
      }}
    >
      {children}
    </ArticlesContext.Provider>
  );
}

export function useArticles() {
  const ctx = useContext(ArticlesContext);
  if (!ctx) throw new Error('useArticles must be used within ArticlesProvider');
  return ctx;
}
